"use client";

import { site } from "@/lib/seo/site";
import Link from "next/link";

/** Aligns with markdown link rules in lib/utils/sanitization.ts — block javascript:, data:, etc. */
const ALLOWED_HREF_SCHEMES = new Set(["http", "https", "mailto", "tel", "sms"]);

function isAllowedCtaHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (h.startsWith("/") || h.startsWith("#") || h.startsWith("?")) return true;
  if (h.startsWith("//")) return true;
  const schemeMatch = h.match(/^([a-z][-a-z0-9+.]*):/i);
  if (schemeMatch) {
    return ALLOWED_HREF_SCHEMES.has(schemeMatch[1].toLowerCase());
  }
  return true;
}

function isDifferentOriginFromApp(u: URL): boolean {
  const siteOrigin = new URL(site.baseUrl).origin;
  if (u.origin === siteOrigin) return false;
  if (typeof window !== "undefined" && u.origin === window.location.origin) return false;
  return true;
}

function isCommSchemeHref(lower: string): boolean {
  return lower.startsWith("mailto:") || lower.startsWith("tel:") || lower.startsWith("sms:");
}

/**
 * True when the href should use a plain <a> (different origin or special schemes),
 * not Next.js <Link> (in-app navigation).
 */
function isExternalLink(href: string): boolean {
  if (!href) return false;
  const h = href.trim();
  // Protocol-relative URLs (//host/path) are absolute, not app routes
  if (h.startsWith("//")) return true;
  // Same-document / same-app query or hash navigation
  if (h.startsWith("#") || h.startsWith("?")) return false;
  if (h.startsWith("/")) return false;

  const lower = h.toLowerCase();
  if (isCommSchemeHref(lower)) return true;

  try {
    return isDifferentOriginFromApp(new URL(h));
  } catch (err) {
    // Unparseable or odd relative strings (e.g. "foo/bar") — avoid Next Link ambiguity
    if (process.env.NODE_ENV === "development") {
      const reason = err instanceof Error ? err.message : String(err);
      const preview = h.length > 80 ? `${h.slice(0, 80)}…` : h;
      console.warn("[CtaPillButton] isExternalLink: URL parse failed; using <a>.", {
        href: preview,
        reason,
      });
    }
    return true;
  }
}

/** Handlers like mailto/tel should not use target="_blank" (avoids an extra blank tab). */
function shouldOpenInNewTab(href: string): boolean {
  const h = href.trim().toLowerCase();
  if (h.startsWith("mailto:") || h.startsWith("tel:") || h.startsWith("sms:")) {
    return false;
  }
  return true;
}

const baseClasses =
  "inline-block rounded-full charlie-kingdom-font font-bold border-2 border-black transition-colors";

const variantStyles = {
  primary: "bg-cta-yellow text-black hover:bg-cta-yellow-hover shadow-cta-yellow",
  orange: "bg-cta-orange text-black hover:bg-cta-orange-light shadow-cta-orange",
  secondary:
    "bg-surface-muted text-black hover:bg-surface-muted-hover shadow-cta-neutral disabled:opacity-60 disabled:hover:bg-surface-muted",
} as const;

const sizeClasses = {
  sm: "px-5 py-2.5 text-sm",
  md: "px-6 py-3 text-lg",
} as const;

export type CtaPillVariant = keyof typeof variantStyles;
export type CtaPillSize = keyof typeof sizeClasses;

interface CtaPillBaseProps {
  variant?: CtaPillVariant;
  size?: CtaPillSize;
  children: React.ReactNode;
  className?: string;
}

type CtaPillLinkProps = CtaPillBaseProps & {
  href: string;
  onClick?: never;
  disabled?: never;
};

type CtaPillButtonOnlyProps = CtaPillBaseProps & {
  href?: never;
  onClick: () => void;
  disabled?: boolean;
};

export type CtaPillButtonProps = CtaPillLinkProps | CtaPillButtonOnlyProps;

export function CtaPillButton({
  variant = "primary",
  size = "md",
  children,
  className = "",
  ...props
}: CtaPillButtonProps) {
  const classes = [baseClasses, variantStyles[variant], sizeClasses[size], className]
    .filter(Boolean)
    .join(" ");

  if ("href" in props && props.href) {
    const href = props.href;
    if (!isAllowedCtaHref(href)) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[CtaPillButton] Blocked disallowed href scheme.", {
          preview: href.trim().slice(0, 120),
        });
      }
      return (
        <span className={classes} aria-disabled="true" title="Link not available">
          <span className="relative -top-0.5">{children}</span>
        </span>
      );
    }
    const isExternal = isExternalLink(href);
    const linkContent = <span className="relative -top-0.5">{children}</span>;
    if (isExternal) {
      const opensNewTab = shouldOpenInNewTab(href);
      return (
        <a
          href={href}
          className={classes}
          {...(opensNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {linkContent}
        </a>
      );
    }
    return (
      <Link href={href} className={classes}>
        {linkContent}
      </Link>
    );
  }

  return (
    <button type="button" onClick={props.onClick} disabled={props.disabled} className={classes}>
      <span className="relative -top-0.5">{children}</span>
    </button>
  );
}
